import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  isOnboardingResponseForProject,
  isProgressionDataLoading,
  progressionQueryOptions
} from '../../utils/progressionQueryUtils';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { getManagementOnboardingMap } from '../../services/api/implementation/impl';
import {
  getManagementOnboardingStepNavigation,
  MANAGEMENT_ONBOARDING_STEP_ACTION_LABELS
} from '../../utils/projectStructurePriorities';
import { ProgressionTimelineBody } from './ProgressionTimelineSteps';

const mapStepStatus = (status) => {
  if (status === 'completed') return 'completed';
  if (status === 'current') return 'active';
  return 'locked';
};

const mapSubStepStatus = (status) => {
  if (status === 'completed') return 'completed';
  if (status === 'available') return 'available';
  return 'locked';
};

const mapApiSubSteps = (subSteps = []) =>
  subSteps.map((subStep) => ({
    id: subStep.id,
    title: subStep.title,
    vertical: subStep.vertical || null,
    status: mapSubStepStatus(subStep.status),
    priorityId: subStep.priorityId,
    owner: subStep.owner?.name?.trim() || null,
    type: subStep.type || null
  }));

const mapApiStepsToStages = (steps = []) =>
  steps.map((step) => {
    const uiStatus = mapStepStatus(step.status);
    const actionLabel =
      step.actionLabel || MANAGEMENT_ONBOARDING_STEP_ACTION_LABELS[step.id] || null;
    const subSteps = mapApiSubSteps(step.subSteps);
    const completedSubSteps =
      step.completedSubSteps ??
      subSteps.filter((subStep) => subStep.status === 'completed').length;
    const totalSubSteps = step.totalSubSteps ?? subSteps.length;

    return {
      id: step.id,
      title: step.title,
      description: step.description || null,
      needsLabel: step.needsLabel || null,
      status: uiStatus,
      phase: step.status,
      owner: step.owner?.name?.trim() || null,
      pendingDays: step.pendingDays,
      tag: step.tag,
      priorityId: step.priorityId,
      completedSubSteps,
      totalSubSteps,
      subSteps,
      actions:
        uiStatus === 'active' && actionLabel && subSteps.length === 0 ? [actionLabel] : []
    };
  });

const MissionProgressionView = ({ projectID = null }) => {
  const navigate = useNavigate();
  const { projects, isProjectsLoading } = useProjectsList();
  const hasProjects = (projects?.length ?? 0) > 0;
  const isFilterReady = projectID !== null && projectID !== undefined;
  const hasProjectSelected = isFilterReady && String(projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(projectID) : null;
  const shouldFetchBuilderLevel = isFilterReady && !hasProjects;
  const shouldFetchProjectLevel = isFilterReady && hasProjects && hasProjectSelected;

  const {
    data: onboardingResponse,
    isPending,
    isFetching,
    isError
  } = useQuery({
    queryKey: [
      'managementOnboardingMap',
      shouldFetchProjectLevel ? resolvedProjectId : 'builder'
    ],
    queryFn: () =>
      getManagementOnboardingMap(shouldFetchProjectLevel ? resolvedProjectId : null),
    enabled: shouldFetchBuilderLevel || shouldFetchProjectLevel,
    ...progressionQueryOptions
  });

  const isInitialLoad = isProgressionDataLoading({
    isPending,
    isFetching,
    response: onboardingResponse,
    projectId: shouldFetchProjectLevel ? resolvedProjectId : null
  });

  const stages = useMemo(() => {
    if (!isOnboardingResponseForProject(
      onboardingResponse,
      shouldFetchProjectLevel ? resolvedProjectId : null
    )) {
      return [];
    }

    return mapApiStepsToStages(onboardingResponse?.data?.steps);
  }, [onboardingResponse, resolvedProjectId, shouldFetchProjectLevel]);

  const progressPercent = isOnboardingResponseForProject(
    onboardingResponse,
    shouldFetchProjectLevel ? resolvedProjectId : null
  )
    ? onboardingResponse?.data?.progress?.percent ?? 0
    : 0;

  const handleStageAction = (stage) => {
    const nav = getManagementOnboardingStepNavigation(resolvedProjectId, stage.id);
    if (!nav) return;

    if (nav.localStorageTab) {
      localStorage.setItem('secondaryTab', nav.localStorageTab);
    }

    navigate(nav.path, { state: nav.state });
  };

  const handleSubStepAction = (subStep) => {
    navigate('/builder/users', {
      state: {
        projectID: resolvedProjectId,
        vertical: subStep.vertical
      }
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-[#1E4A53]">Mission Progression</h3>
          {hasProjectSelected && (
            <p className="mt-0.5 text-xs text-gray-500">Project-specific onboarding progress</p>
          )}
        </div>
        {!isInitialLoad &&
          !isError &&
          !isProjectsLoading &&
          !(hasProjects && !hasProjectSelected) && (
          <div className="shrink-0 text-right">
            <p className="text-2xl font-bold leading-none text-[#0f2a45]">{progressPercent}%</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Complete
            </p>
          </div>
        )}
      </div>

      {!hasProjectSelected && (
        <p className="mb-4 text-xs font-medium uppercase tracking-wide text-[#2A565E]">
          Builder Level
        </p>
      )}

      {(isInitialLoad || isProjectsLoading || (hasProjects && !hasProjectSelected)) && (
        <div className="py-12 text-center text-sm text-gray-500">Loading mission progression...</div>
      )}

      {isError && !isProjectsLoading && !(hasProjects && !hasProjectSelected) && (
        <div className="py-12 text-center text-sm text-rose-600">
          Unable to load mission progression. Please try again.
        </div>
      )}

      {!isInitialLoad &&
        !isError &&
        !isProjectsLoading &&
        !(hasProjects && !hasProjectSelected) &&
        stages.length === 0 && (
        <div className="py-12 text-center text-sm text-gray-500">
          {hasProjectSelected
            ? 'No onboarding steps available for this project yet.'
            : 'No builder-level onboarding steps available yet.'}
        </div>
      )}

      {!isInitialLoad &&
        !isError &&
        !isProjectsLoading &&
        !(hasProjects && !hasProjectSelected) &&
        stages.length > 0 && (
        <ProgressionTimelineBody
          stages={stages}
          onAction={handleStageAction}
          onSubStepAction={handleSubStepAction}
        />
      )}
    </div>
  );
};

export default MissionProgressionView;
